/* Approved UI behavior; existing Shopify cart and checkout handlers remain authoritative. */
(() => {
  'use strict';
  const all = (s, root = document) => [...root.querySelectorAll(s)];
  const shown = el => !!el && el.isConnected && !el.closest('[inert]') &&
    getComputedStyle(el).visibility !== 'hidden' && el.getClientRects().length > 0;
  const focusables = root => all('a[href],button,input,select,textarea,[tabindex="0"]', root)
    .filter(el => shown(el) && !el.disabled);
  let lastControl = null, currentModal = null, scheduled = false;
  const returnFocus = new Map(), inerted = [];
  let previousBodyOverflow = null;

  function focusTarget(el) {
    if (shown(el)) el.focus({preventScroll: true});
  }
  function focusWhenVisible(resolve) {
    const until = performance.now() + 1200;
    function attempt() {
      const el = resolve();
      if (shown(el)) focusTarget(el);
      else if (performance.now() < until) requestAnimationFrame(attempt);
    }
    requestAnimationFrame(attempt);
  }
  function clearInert() {
    inerted.splice(0).forEach(el => { if (el.isConnected) el.inert = false; });
  }
  function isolate(dialog) {
    for (let branch = dialog; branch && branch.parentElement; branch = branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch && !sibling.inert && !['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) {
          sibling.inert = true; inerted.push(sibling);
        }
      }
      if (branch.parentElement === document.body) break;
    }
  }
  function syncModal() {
    scheduled = false;
    const dialogs = all('[data-ux-modal]');
    const open = dialogs.filter(el => el.dataset.uxOpen === 'true');
    const next = open.at(-1) || null;
    if (next === currentModal) return;
    const previous = currentModal;
    clearInert();
    dialogs.forEach(el => { el.inert = el.dataset.uxOpen !== 'true'; });
    currentModal = next;
    if (next) {
      if (next.dataset.uxModal === 'detail') next.scrollTop = 0;
      if (!returnFocus.has(next)) returnFocus.set(next, lastControl?.isConnected ? lastControl : document.activeElement);
      isolate(next);
      if (previousBodyOverflow === null) previousBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      const target = previous && returnFocus.get(previous);
      focusWhenVisible(() => currentModal === next ? (target && next.contains(target) ? target : (focusables(next)[0] || next)) : null);
    } else {
      if (previousBodyOverflow !== null) document.body.style.overflow = previousBodyOverflow;
      previousBodyOverflow = null;
      focusTarget(returnFocus.get(previous));
    }
    for (const el of dialogs) if (el.dataset.uxOpen !== 'true') returnFocus.delete(el);
  }
  function scheduleModal() {
    if (!scheduled) { scheduled = true; requestAnimationFrame(syncModal); }
  }
  const observer = new MutationObserver(records => {
    if (records.some(r => r.type === 'attributes' || [...r.addedNodes].some(n =>
      n.nodeType === 1 && (n.matches('[data-ux-modal]') || n.querySelector('[data-ux-modal]'))))) scheduleModal();
  });
  observer.observe(document.body, {subtree: true, childList: true, attributes: true, attributeFilter: ['data-ux-open']});
  scheduleModal();

  const message = document.createElement('div');
  message.className = 'ux-notice'; message.hidden = true;
  message.innerHTML = '<span role="status" aria-live="polite" aria-atomic="true"></span><a href="/pages/cart?view=mp2-cart">view cart →</a><button type="button" aria-label="Dismiss notification">×</button>';
  document.body.append(message);
  const status = message.querySelector('[role="status"]');
  let noticeTimer;
  const dismiss = () => {
    const restore = message.contains(document.activeElement);
    message.hidden = true; clearTimeout(noticeTimer);
    if (restore) focusTarget(lastControl);
  };
  const armTimer = () => { clearTimeout(noticeTimer); noticeTimer = setTimeout(() => {
    if (!message.matches(':hover') && !message.contains(document.activeElement)) dismiss();
  }, 5000); };
  function notify(text, withCart = false) {
    (currentModal || document.body).append(message);
    message.inert = false;
    message.hidden = false;
    status.textContent = text;
    message.querySelector('a').hidden = !withCart;
    armTimer();
  }
  message.querySelector('button').addEventListener('click', dismiss);
  message.addEventListener('mouseenter', () => clearTimeout(noticeTimer));
  message.addEventListener('mouseleave', armTimer);
  message.addEventListener('focusin', () => clearTimeout(noticeTimer));
  message.addEventListener('focusout', armTimer);
  window.addEventListener('mp2:notice', e => notify(e.detail));
  let priorCart = {};
  try { priorCart = JSON.parse(localStorage.getItem('mp2-shop-cart-v1') || '{}'); } catch (_) {}
  window.addEventListener('mp2:cart-change', event => {
    const cart = event.detail.cart || {};
    if (JSON.stringify(cart) === JSON.stringify(priorCart)) return;
    const total = Object.values(cart).reduce((a, b) => a + Number(b), 0);
    const added = Object.keys(cart).some(id => cart[id] > (priorCart[id] || 0));
    const context = total === 1 ? '1 item in your cart.' : `${total} items in your cart.`;
    const removed = Object.keys(priorCart).some(id => priorCart[id] && !cart[id]);
    const verb = added ? (/add/i.test(lastControl?.dataset.uxAction || '') ? 'Added to cart. ' : 'Quantity updated. ') : removed ? 'Item removed. ' : 'Quantity updated. ';
    notify(verb + context, true);
    priorCart = {...cart};
    // A removed row no longer exists to receive focus; keep the user in the cart.
    requestAnimationFrame(() => {
      if (lastControl && !lastControl.isConnected && document.activeElement === document.body) {
        const next = all('[data-ux-action="it.remove"]').find(shown) || all('h1,h2').find(shown);
        if (next) { if (!next.hasAttribute('tabindex')) next.tabIndex = -1; focusTarget(next); }
      }
    });
  });

  document.addEventListener('click', event => {
    const el = event.target.closest('[data-ux-action]');
    if (!el) return;
    lastControl = el;
    if (el.dataset.uxAction === 'l.pick') {
      const trigger = el.parentElement.parentElement.querySelector('[data-ux-disclosure]') ||
        el.closest('[style*="position: relative"],[style*="position:relative"]')?.querySelector('[data-ux-disclosure]');
      requestAnimationFrame(() => focusTarget(trigger));
    }
  }, true);

  document.addEventListener('keydown', event => {
    const el = event.target.closest('[data-ux-action]');
    if (currentModal && event.key === 'Tab') {
      const items = focusables(currentModal), first = items[0], last = items.at(-1);
      if (!first) { event.preventDefault(); focusTarget(currentModal); return; }
      if (event.shiftKey && (document.activeElement === first || !currentModal.contains(document.activeElement))) {
        event.preventDefault(); focusTarget(last); return;
      }
      if (!event.shiftKey && (document.activeElement === last || !currentModal.contains(document.activeElement))) {
        event.preventDefault(); focusTarget(first); return;
      }
    }
    if (event.key === 'Escape') {
      const dropdown = all('[data-ux-disclosure][aria-expanded="true"]').find(shown);
      if (dropdown) { event.preventDefault(); dropdown.click(); focusTarget(dropdown); return; }
      if (currentModal) {
        const action = {nav: 'closeNav', kit: 'closeAddons', detail: 'closeExpand'}[currentModal.dataset.uxModal];
        const close = all(`[data-ux-action="${action}"]`, currentModal).find(shown);
        if (close) { event.preventDefault(); close.click(); }
        return;
      }
      if (!message.hidden) dismiss();
    }
    if (!el) return;
    if (el.dataset.uxDisclosure && ['ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      if (el.getAttribute('aria-expanded') !== 'true') el.click();
      focusWhenVisible(() => {
        const choices = all('[data-ux-action="l.pick"]', el.parentElement).filter(shown);
        return event.key === 'ArrowUp' ? choices.at(-1) : choices[0];
      });
      return;
    }
    if (el.dataset.uxAction === 'l.pick' && ['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      const choices = all('[data-ux-action="l.pick"]', el.parentElement).filter(shown);
      const index = choices.indexOf(el);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? choices.length - 1 :
        (index + (event.key === 'ArrowDown' ? 1 : -1) + choices.length) % choices.length;
      event.preventDefault(); focusTarget(choices[next]); return;
    }
    if (el.dataset.uxKeyboard && ['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      if (!event.repeat) el.click();
    }
  });
  document.addEventListener('focusin', event => {
    if (currentModal && !currentModal.contains(event.target)) focusTarget(focusables(currentModal)[0] || currentModal);
  });

  const skip = document.createElement('a');
  skip.className = 'ux-skip'; skip.href = '#ux-main'; skip.textContent = 'Skip to content';
  skip.addEventListener('click', event => {
    const heading = all('h1,h2').find(shown);
    if (heading) {
      event.preventDefault(); heading.tabIndex = -1; heading.focus();
      heading.scrollIntoView({block: 'start', behavior: 'instant'});
    }
  });
  document.body.prepend(skip);
  window.addEventListener('hashchange', () => {
    if (!/^#(?:shop|cart|product-|blog|contact|terms|privacy|returns)/.test(location.hash)) return;
    requestAnimationFrame(() => {
      const heading = all('h1,h2').find(shown);
      if (heading) { heading.tabIndex = -1; focusTarget(heading); }
    });
  });
})();
